import { useEffect, useState } from "react";
import { readAndRemoveUrlParams } from "../utils/urlParamUtils";

/**
 * Attribution data is stored in memory only for ePrivacy compliance.
 * Data is lost on page refresh (intentional).
 *
 * Google Ads click IDs (gclid, gbraid, wbraid, dclid, gad_*) are NOT stored.
 * Instead, when ad params are detected, we infer generic UTM params (google_ads/cpc)
 * for anonymous, aggregate analytics. If user grants marketing consent, gtag handles
 * the click IDs for conversion tracking - we don't need to track them ourselves.
 */

const UTM_PARAMS = [
  "ref",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type AttributionTouch = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  initialPath: string | null;
  referrer: string | null;
};

// Module-level storage (in-memory only, lost on page refresh)
let storedAttribution: AttributionTouch | null = null;

export interface AttributionData {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  initialPath: string | null;
  referrer: string | null;
  isPaidAds: boolean;
}

/**
 * Parse attribution data from URL params.
 * Exported for testing purposes.
 *
 * Only removes UTM params from URL. Google Ads params (gclid, etc.) are read
 * but NOT removed - gtag needs them and handles their lifecycle.
 * Any other params are preserved.
 */
export function parseAttributionFromUrl(
  url: string,
  pathname: string,
  referrer: string,
): {
  attribution: AttributionTouch | null;
  newUrl: string;
  hasParams: boolean;
} {
  // Only remove UTM params from URL (not Google Ads params)
  const { params: utmParams, newUrl } = readAndRemoveUrlParams(url, [
    ...UTM_PARAMS,
  ]);

  // Read Google Ads params separately (without removing them)
  const urlObj = new URL(url, "http://placeholder");
  const adClickParams = {
    gclid: urlObj.searchParams.get("gclid"),
    gbraid: urlObj.searchParams.get("gbraid"),
    wbraid: urlObj.searchParams.get("wbraid"),
    dclid: urlObj.searchParams.get("dclid"),
    gad_source: urlObj.searchParams.get("gad_source"),
    gad_campaignid: urlObj.searchParams.get("gad_campaignid"),
  };

  const hasRef = utmParams.ref !== null;
  const hasUtm = Object.entries(utmParams).some(
    ([key, value]) => key.startsWith("utm_") && value !== null,
  );
  const hasAdClick = Object.values(adClickParams).some(
    (value) => value !== null && value !== "",
  );

  if (!hasRef && !hasUtm && !hasAdClick) {
    return { attribution: null, newUrl, hasParams: false };
  }

  let source = utmParams.utm_source;
  let medium = utmParams.utm_medium;
  const campaign = utmParams.utm_campaign;
  const content = utmParams.utm_content;
  const term = utmParams.utm_term;

  if (hasRef && !source) {
    source = utmParams.ref;
  }

  if (hasAdClick) {
    source = "google_ads";
    medium = "cpc";
  }

  const attribution: AttributionTouch = {
    source,
    medium,
    campaign,
    content,
    term,
    initialPath: pathname,
    referrer: referrer || null,
  };

  return { attribution, newUrl, hasParams: true };
}

/**
 * Capture attribution data from URL params and store in memory.
 * Uses first-touch attribution (no overwrite).
 * Removes UTM params from URL after capturing.
 *
 * Google Ads click IDs are detected but NOT stored - instead we infer
 * generic source/medium (google_ads/cpc) for aggregate analytics.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  const { attribution, newUrl, hasParams } = parseAttributionFromUrl(
    window.location.href,
    window.location.pathname,
    document.referrer,
  );

  if (!hasParams) {
    return;
  }

  // First-touch: only capture if not already stored
  if (storedAttribution) {
    return;
  }

  storedAttribution = attribution;

  if (newUrl !== window.location.href) {
    window.history.replaceState({}, "", newUrl);
  }
}

/**
 * Get attribution data from memory.
 * Returns defaults if no attribution data is stored.
 */
export function getAttribution(): AttributionData {
  // Capture here in case it wasn't captured earlier. Capture is done in
  // onInitialClientRender in gatsby-browser.tsx, but getAttribution may
  // be called before that.
  captureAttribution();

  if (typeof window === "undefined") {
    return getDefaultAttribution();
  }

  if (!storedAttribution) {
    return getDefaultAttribution();
  }

  return expandAttribution(storedAttribution);
}

function getDefaultAttribution(): AttributionData {
  const touch = getDefaultTouch();
  return expandAttribution(touch);
}

function getDefaultTouch(): AttributionTouch {
  if (typeof window === "undefined") {
    return {
      source: null,
      medium: null,
      campaign: null,
      content: null,
      term: null,
      initialPath: null,
      referrer: null,
    };
  }

  const { source, medium } = inferSourceFromReferrer(
    document.referrer,
    window.location.hostname,
  );
  const referrer = document.referrer;

  return {
    source,
    medium,
    campaign: null,
    content: null,
    term: null,
    initialPath: window.location.pathname,
    referrer: referrer || null,
  };
}

function expandAttribution(touch: AttributionTouch): AttributionData {
  const isPaidAds = isPaidAdsSession(touch);

  return {
    source: touch.source,
    medium: touch.medium,
    campaign: touch.campaign,
    content: touch.content,
    term: touch.term,
    initialPath: touch.initialPath,
    referrer: touch.referrer,
    isPaidAds,
  };
}

/**
 * Determine if a session is from paid ads based on source and medium.
 * Exported for testing.
 */
export function isPaidAdsSession(touch: {
  source: string | null;
  medium: string | null;
}): boolean {
  return (
    (touch.source === "google" || touch.source === "google_ads") &&
    touch.medium === "cpc"
  );
}

/**
 * Infer source and medium from a referrer URL.
 * Exported for testing.
 */
export function inferSourceFromReferrer(
  referrer: string,
  currentHostname: string,
): { source: string | null; medium: string | null } {
  let source: string | null = null;
  let medium: string | null = null;

  if (referrer) {
    try {
      const referrerUrl = new URL(referrer);
      const hostname = referrerUrl.hostname;

      // Check for known referrers
      if (hostname.includes("epassikuva")) {
        source = "epassikuva";
        medium = "referral";
      } else if (hostname.includes("google")) {
        source = "google";
        medium = "organic";
      } else if (hostname !== currentHostname) {
        source = hostname;
        medium = "referral";
      }
    } catch {
      // Invalid referrer URL
    }
  }

  if (!source) {
    source = "direct";
  }

  return { source, medium };
}

/**
 * Append attribution UTM params to a URL for cross-tab tracking.
 * Preserves existing query params and only adds UTM params if attribution data exists.
 * Does NOT append Google Ads click IDs - gtag handles those if consent is granted.
 *
 * WARNING: You probably should NOT use this directly in components - use useAttributedUrl() instead.
 * Direct usage may cause attributes to be missing because of Gatsby SSR.
 */
export function appendAttributionToUrl(url: string): string {
  const attribution = getAttribution();

  const hasTouch =
    !!attribution.source ||
    !!attribution.medium ||
    !!attribution.campaign ||
    !!attribution.content ||
    !!attribution.term;

  // Don't append if no attribution data (only defaults like "direct")
  if (!hasTouch || (attribution.source === "direct" && !attribution.medium)) {
    return url;
  }

  const urlObj = new URL(url, window.location.origin);

  if (attribution.source && attribution.source !== "direct") {
    urlObj.searchParams.set("utm_source", attribution.source);
  }
  if (attribution.medium) {
    urlObj.searchParams.set("utm_medium", attribution.medium);
  }
  if (attribution.campaign) {
    urlObj.searchParams.set("utm_campaign", attribution.campaign);
  }
  if (attribution.content) {
    urlObj.searchParams.set("utm_content", attribution.content);
  }
  if (attribution.term) {
    urlObj.searchParams.set("utm_term", attribution.term);
  }

  return urlObj.pathname + urlObj.search + urlObj.hash;
}

/**
 * A hook to generate links that include attribution parameters.
 *
 * This MUST be done in a hook (client-side) to avoid hydration mismatches
 * with Gatsby SSG. During build (server-side), the link will be clean.
 * After mount (client-side), it will update to include the params.
 */
export function useAttributedUrl(targetUrl: string): string {
  const [attributedUrl, setAttributedUrl] = useState(targetUrl);

  useEffect(() => {
    // This runs only on the client, ensuring window is available
    // and attribution has been captured/restored.
    setAttributedUrl(appendAttributionToUrl(targetUrl));
  }, [targetUrl]);

  return attributedUrl;
}

/**
 * Reset attribution data. Primarily for testing purposes.
 */
export function resetAttribution(): void {
  storedAttribution = null;
}

/**
 * Set stored attribution directly. For testing purposes only.
 */
export function setStoredAttributionForTest(data: {
  source: string | null;
  medium: string | null;
  campaign?: string | null;
  content?: string | null;
  term?: string | null;
  initialPath?: string | null;
  referrer?: string | null;
}): void {
  storedAttribution = {
    source: data.source,
    medium: data.medium,
    campaign: data.campaign ?? null,
    content: data.content ?? null,
    term: data.term ?? null,
    initialPath: data.initialPath ?? null,
    referrer: data.referrer ?? null,
  };
}
